import { useEffect, useMemo, useState } from "react";
import Layout from "@/components/Layout";
import api, { apiErr } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { DatePicker } from "@/components/ui/date-picker";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { CalendarDays, Plus, Clock3, CheckCircle2, XCircle, Info, AlertTriangle } from "lucide-react";
import { toast } from "sonner";

export default function Leaves({ embedded = false }) {
  const [bal, setBal] = useState(null);
  const [data, setData] = useState({ pending: [], recent: [] });
  const [form, setForm] = useState({ start_date: "", end_date: "", reason: "" });
  const [preview, setPreview] = useState(null);
  const [previewError, setPreviewError] = useState("");
  const [saving, setSaving] = useState(false);

  const load = async () => {
    try {
      const [b, l] = await Promise.all([api.get("/leaves/balance"), api.get("/leaves")]);
      setBal(b.data);
      setData(l.data);
    } catch (e) {
      toast.error("Could not load leaves");
    }
  };
  useEffect(() => { load(); }, []);

  // Live working-day preview whenever dates change.
  useEffect(() => {
    if (!form.start_date || !form.end_date) { setPreview(null); setPreviewError(""); return; }
    const ctrl = new AbortController();
    (async () => {
      try {
        const { data } = await api.post(
          "/leaves/preview",
          { start_date: form.start_date, end_date: form.end_date },
          { signal: ctrl.signal },
        );
        setPreview(data);
        setPreviewError("");
      } catch (e) {
        if (e.code === "ERR_CANCELED") return;
        setPreview(null);
        setPreviewError(apiErr(e.response?.data?.detail) || "Invalid range");
      }
    })();
    return () => ctrl.abort();
  }, [form.start_date, form.end_date]);

  const submit = async () => {
    if (!form.start_date || !form.end_date || !form.reason.trim()) { toast.error("All fields required."); return; }
    if (preview?.exceeds_balance) { toast.error("Not enough leave balance for this range."); return; }
    setSaving(true);
    try {
      await api.post("/leaves", form);
      toast.success("Leave request sent to admin.");
      setForm({ start_date: "", end_date: "", reason: "" });
      setPreview(null);
      await load();
    } catch (e) {
      toast.error(apiErr(e.response?.data?.detail) || "Could not apply");
    } finally { setSaving(false); }
  };

  const minDate = useMemo(() => new Date().toISOString().slice(0, 10), []);

  const content = (
    <>
      {!embedded && (
        <div className="mb-6">
          <div className="text-xs uppercase tracking-[0.2em] text-slate-400 mb-2">Leave</div>
          <h1 className="text-3xl font-bold text-slate-900 dark:text-slate-100" style={{ fontFamily: "Outfit" }}>My leaves</h1>
          {bal?.year_start && (
            <div className="text-xs text-slate-500 mt-1">Academic year started {bal.year_start} · 12 days/year · Sundays not counted.</div>
          )}
        </div>
      )}

      {bal && (
        <div className="grid sm:grid-cols-4 gap-4 mb-6">
          <Stat label="Entitlement" value={bal.entitlement} suffix="days" tone="sky" testid="leave-stat-entitlement" />
          <Stat label="Approved" value={bal.used} suffix="days" tone="emerald" testid="leave-stat-used" />
          <Stat label="Pending" value={bal.pending} suffix="days" tone="amber" testid="leave-stat-pending" />
          <Stat label="Remaining" value={bal.remaining} suffix="days" tone={bal.remaining > 3 ? "violet" : "rose"} testid="leave-stat-remaining" />
        </div>
      )}

      <div className="bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-2xl p-5 mb-6">
        <div className="text-sm font-semibold mb-3" style={{ fontFamily: "Outfit" }}>Apply for leave</div>
        <div className="grid sm:grid-cols-4 gap-3">
          <div>
            <Label className="text-xs uppercase tracking-[0.15em] text-slate-500">Start</Label>
            <DatePicker value={form.start_date} min={minDate} onChange={(v) => setForm({ ...form, start_date: v })} className="mt-1" data-testid="leave-start" />
          </div>
          <div>
            <Label className="text-xs uppercase tracking-[0.15em] text-slate-500">End</Label>
            <DatePicker value={form.end_date} min={form.start_date || minDate} onChange={(v) => setForm({ ...form, end_date: v })} className="mt-1" data-testid="leave-end" />
          </div>
          <div className="sm:col-span-2">
            <Label className="text-xs uppercase tracking-[0.15em] text-slate-500">Reason</Label>
            <Textarea value={form.reason} onChange={(e) => setForm({ ...form, reason: e.target.value })} placeholder="e.g. Family event" rows={1} className="mt-1 resize-none" data-testid="leave-reason" />
          </div>
        </div>

        {(preview || previewError) && (
          <div
            className={
              "mt-3 rounded-xl px-4 py-3 text-sm flex items-start gap-2 " +
              (previewError
                ? "bg-rose-50 dark:bg-rose-900/20 text-rose-700 dark:text-rose-300"
                : preview?.exceeds_balance
                  ? "bg-rose-50 dark:bg-rose-900/20 text-rose-700 dark:text-rose-300"
                  : "bg-sky-50 dark:bg-sky-900/20 text-sky-800 dark:text-sky-200")
            }
            data-testid="leave-preview"
          >
            {previewError || preview?.exceeds_balance ? <AlertTriangle className="h-4 w-4 mt-0.5 shrink-0" /> : <Info className="h-4 w-4 mt-0.5 shrink-0" />}
            {previewError ? (
              <div>{previewError}</div>
            ) : (
              <div className="leading-relaxed">
                <span data-testid="leave-preview-working-days" className="font-semibold">{preview.working_days} working day{preview.working_days === 1 ? "" : "s"}</span>
                {preview.sundays_skipped > 0 && <span className="text-xs ml-1">({preview.sundays_skipped} Sunday{preview.sundays_skipped === 1 ? "" : "s"} skipped)</span>}
                <span className="mx-2">·</span>
                <span data-testid="leave-preview-balance">{preview.available_before} day(s) available before · {preview.available_after} after</span>
                {preview.exceeds_balance && <span className="ml-2 font-semibold">— exceeds your balance</span>}
              </div>
            )}
          </div>
        )}

        <Button
          onClick={submit}
          disabled={saving || !!previewError || !preview || preview.exceeds_balance || preview.working_days <= 0}
          className="mt-4 bg-sky-500 hover:bg-sky-600"
          data-testid="leave-submit"
        >
          <Plus className="h-4 w-4 mr-1" /> {saving ? "Sending…" : "Apply"}
        </Button>
      </div>

      <Section title="Pending" rows={data.pending} testid="leave-pending" />
      <Section title="History" rows={data.recent.filter((r) => r.status !== "pending")} testid="leave-history" />
    </>
  );

  if (embedded) return content;
  return <Layout>{content}</Layout>;
}

function Stat({ label, value, suffix, tone, testid }) {
  const cls = { sky: "text-sky-600", amber: "text-amber-600", emerald: "text-emerald-600", rose: "text-rose-600", violet: "text-violet-600" }[tone];
  return (
    <div className="bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-2xl p-5" data-testid={testid}>
      <div className="text-[10px] uppercase tracking-[0.2em] text-slate-400 mb-1">{label}</div>
      <div className={`text-4xl font-bold ${cls}`} style={{ fontFamily: "Outfit" }}>
        {value}<span className="text-sm text-slate-400 font-normal ml-1">{suffix}</span>
      </div>
    </div>
  );
}

function Section({ title, rows, testid }) {
  if (!rows.length) return null;
  return (
    <div className="bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-2xl overflow-hidden mb-4" data-testid={testid}>
      <div className="px-5 py-3 border-b border-slate-100 text-xs uppercase tracking-[0.15em] text-slate-500">{title}</div>
      <table className="w-full text-sm">
        <tbody>
          {rows.map((r) => (
            <tr key={r.id} className="border-b border-slate-100 dark:border-slate-800 last:border-0">
              <td className="px-5 py-3"><CalendarDays className="h-4 w-4 text-slate-400 inline mr-1" /> {r.start_date} → {r.end_date} <span className="text-xs text-slate-400">({r.days} working day{r.days === 1 ? "" : "s"})</span></td>
              <td className="px-5 py-3 text-slate-600 dark:text-slate-300">{r.reason}</td>
              <td className="px-5 py-3 text-right">
                {r.status === "pending" && <span className="inline-flex items-center gap-1 text-amber-600 text-xs"><Clock3 className="h-3 w-3" />Pending</span>}
                {r.status === "approved" && <span className="inline-flex items-center gap-1 text-emerald-600 text-xs"><CheckCircle2 className="h-3 w-3" />Approved</span>}
                {r.status === "rejected" && <span className="inline-flex items-center gap-1 text-rose-600 text-xs"><XCircle className="h-3 w-3" />Rejected</span>}
                {r.replacement_teacher_name && <div className="text-[10px] text-emerald-600 mt-1">Sub: {r.replacement_teacher_name}</div>}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}
